import React, { useState, useMemo } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  ListItemText,
  Box,
  TextField,
  InputAdornment,
  FormHelperText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

interface ClassFiscalOption {
  value: string;
  label: string;
}

interface ClassFiscalSelectProps {
  value?: string | string[] | null;
  onChange: (value: string | string[] | null) => void;
  label?: string;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  placeholder?: string;
  allowClear?: boolean;
  fullWidth?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
}

const CLASS_FISCAL_OPTIONS: ClassFiscalOption[] = [
  { value: '', label: '---------' },
  { value: '0100101', label: '0100101 - Assinatura de serviços de telefonia' },
  { value: '0100201', label: '0100201 - Assinatura de serviços de comunicação de dados' },
  { value: '0100301', label: '0100301 - Assinatura de serviços de TV por Assinatura' },
  { value: '0100401', label: '0100401 - Assinatura de serviços de comunicação multimídia' },
  { value: '0200101', label: '0200101 - Habilitação de serviços de telefonia' },
  { value: '0200201', label: '0200201 - Habilitação de serviços de comunicação de dados' },
  { value: '0200301', label: '0200301 - Habilitação de TV por Assinatura' },
  { value: '0300101', label: '0300101 - Serviço Medido - Chamadas locais' },
  { value: '0300102', label: '0300102 - Serviço Medido - Chamadas longa distância nacional' },
  { value: '0300103', label: '0300103 - Serviço Medido - Chamadas longa distância internacionais' },
  { value: '0300104', label: '0300104 - Serviço Medido - Chamadas originadas em Roaming' },
  { value: '0300105', label: '0300105 - Serviço Medido - Chamadas recebidas em Roaming' },
  { value: '0300106', label: '0300106 - Serviço Medido - Adicional de chamada' },
  { value: '0300107', label: '0300107 - Serviço Medido - Números Especiais (0300/0500/0600/0800/etc.)' },
  { value: '0300108', label: '0300108 - Serviço Medido - Mensagem SMS' },
  { value: '0300109', label: '0300109 - Serviço Medido - Mensagem MMS' },
  { value: '0300201', label: '0300201 - Serviço Medido - Comunicação de dados' },
  { value: '0300301', label: '0300301 - Serviço Medido - Pay-per-view (programação TV)' },
  { value: '0300401', label: '0300401 - Serviço Medido - Comunicação multimidia' },
  { value: '0400101', label: '0400101 - Serviço Não Medido - Telefonia' },
  { value: '0400201', label: '0400201 - Serviço Não Medido - Comunicação de dados' },
  { value: '0400301', label: '0400301 - Serviço Não Medido - TV por Assinatura' },
  { value: '0400401', label: '0400401 - Serviço Não Medido - Provimento à internet' },
  { value: '0400501', label: '0400501 - Serviço Não Medido - Comunicação multimídia' },
  { value: '0450101', label: '0450101 - Serviços Combinados (dados, voz, mensagens e outros)' },
  { value: '0500101', label: '0500101 - Cartão Telefônico - Telefonia Fixa' },
  { value: '0500102', label: '0500102 - Carga / Recarga de Créditos - Telefonia Fixa' },
  { value: '0500201', label: '0500201 - Carga / Recarga de Créditos - Telefonia Móvel' },
  { value: '0500301', label: '0500301 - Carga / Recarga de Créditos - Serviço de Comunicação Multimídia' },
  { value: '0500401', label: '0500401 - Carga / Recarga de Créditos - TV por assinatura' },
  { value: '0500501', label: '0500501 - Recarga de Créditos - Antecipação' },
  { value: '0500601', label: '0500601 - Repasse de Pré-pago' },
  { value: '0600101', label: '0600101 - Facilidades Adicionais (identificador, caixa postal, não-perturbe, etc)' },
  { value: '0600201', label: '0600201 - Streaming de vídeo e audio' },
  { value: '0600301', label: '0600301 - Serviço de Rastreamento' },
  { value: '0600401', label: '0600401 - Veiculação de publicidade e propaganda em qualquer meio' },
  { value: '0600501', label: '0600501 - Outros Serviços (substituição de número, troca de aparelho, instalação, software, visita técnica, etc.)' },
  { value: '0600601', label: '0600601 - Outros serviços de valor adicionado' },
  { value: '0700101', label: '0700101 - Interconexão' },
  { value: '0700201', label: '0700201 - Roaming' },
  { value: '0700301', label: '0700301 - Exploração Industrial de Linha Dedicada - EILD' },
  { value: '0700401', label: '0700401 - Lançamento de ICMS proporcional às saídas isentas, não tributadas ou com redução de base de cálculo (§ 1º, Cláusula terceira, Convênio ICMS 17/2013)' },
  { value: '0700501', label: '0700501 - Lançamento de ICMS proporcional às cessões de meio destinadas a consumo próprio (§ 1º Cláusula terceira, Convênio ICMS 17/2013)' },
  { value: '0700601', label: '0700601 - Lançamento de ICMS complementar, na condição de responsável tributário' },
  { value: '0800101', label: '0800101 - Aparelho telefônico' },
  { value: '0800201', label: '0800201 - Aparelho Identificador de chamadas' },
  { value: '0800301', label: '0800301 - Modem' },
  { value: '0800401', label: '0800401 - Rack' },
  { value: '0800501', label: '0800501 - Sala/Recinto' },
  { value: '0800601', label: '0800601 - Roteador' },
  { value: '0800701', label: '0800701 - Servidor' },
  { value: '0800801', label: '0800801 - Multiplexador' },
  { value: '0800901', label: '0800901 - Decodificador/Conversor' },
  { value: '0801001', label: '0801001 - Outros equipamentos' },
  { value: '1000101', label: '1000101 - Cobrança de seguros' },
  { value: '1000201', label: '1000201 - Cobrança de parcelamento' },
  { value: '1000301', label: '1000301 - Cobrança de juros de mora' },
  { value: '1000401', label: '1000401 - Cobrança de multa de mora' },
  { value: '1000402', label: '1000402 - Cobrança de multa por descumprimento de contrato (fidelização)' },
  { value: '1000501', label: '1000501 - Cobrança de conta de meses anteriores' },
  { value: '1000601', label: '1000601 - Correção monetária' },
  { value: '1000701', label: '1000701 - Taxas' },
  { value: '1100101', label: '1100101 - Cobrança de Serviços de Terceiros' },
  { value: '1100201', label: '1100201 - Cobrança de Seguros' },
  { value: '1100301', label: '1100301 - Cobrança de Juros de Mora' },
  { value: '1100401', label: '1100401 - Cobrança de Multa de Mora' },
  { value: '1100501', label: '1100501 - Cobrança de Conta de meses anteriores' },
  { value: '1100601', label: '1100601 - Correção Monetária' },
  { value: '1100701', label: '1100701 - Doações' },
  { value: '1200101', label: '1200101 - Item lançado em outra NFCom - Cobrança centralizada' },
  { value: '1300101', label: '1300101 - Item lançado em outra NFCom - Cobrança por cofaturamento' },
  { value: '5900101', label: '5900101 - Dedução relativa a impugnação de serviços' },
  { value: '5900201', label: '5900201 - Dedução referente ajuste de conta' },
  { value: '5900301', label: '5900301 - Dedução relativa à multa pela interrupção de fornecimento' },
  { value: '5900401', label: '5900401 - Dedução por pagamento em duplicidade' },
  { value: '5900501', label: '5900501 - Outras deduções' },
];

const ClassFiscalSelect: React.FC<ClassFiscalSelectProps> = ({
  value,
  onChange,
  label = 'Classe Fiscal',
  size = 'medium',
  error = false,
  helperText,
  required = false,
  disabled = false,
  multiple = false,
  placeholder = 'Selecione uma classe fiscal',
  allowClear = false,
  fullWidth = true,
  variant = 'outlined',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  // Filtrar opções baseado no termo de busca
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return CLASS_FISCAL_OPTIONS;
    
    const term = searchTerm.toLowerCase();
    return CLASS_FISCAL_OPTIONS.filter(option =>
      option.label.toLowerCase().includes(term) ||
      option.value.toLowerCase().includes(term)
    );
  }, [searchTerm]);

  const handleChange = (event: any) => {
    const selectedValue = event.target.value;
    
    if (multiple) {
      // Para seleção múltipla
      if (Array.isArray(selectedValue)) {
        onChange(selectedValue.length > 0 ? selectedValue : null);
      } else if (typeof selectedValue === 'string') {
        onChange([selectedValue]);
      } else {
        onChange(null);
      }
    } else {
      // Para seleção única
      onChange(selectedValue === '' ? null : selectedValue);
    }
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(null);
  };

  const handleRemoveChip = (optionValue: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (multiple && Array.isArray(value)) {
      const newValue = value.filter(v => v !== optionValue);
      onChange(newValue.length > 0 ? newValue : null);
    }
  };

  const getSelectedOptions = (): ClassFiscalOption[] => {
    if (!value) return [];
    
    if (multiple) {
      const arrayValue = Array.isArray(value) ? value : [];
      return CLASS_FISCAL_OPTIONS.filter(option => 
        arrayValue.includes(option.value) && option.value !== ''
      );
    } else if (!multiple && typeof value === 'string' && value !== '') {
      const option = CLASS_FISCAL_OPTIONS.find(o => o.value === value);
      return option ? [option] : [];
    }
    
    return [];
  };

  const renderValue = (selected: any) => {
    if (!selected || (Array.isArray(selected) && selected.length === 0) || selected === '') {
      return <span style={{ color: '#999' }}>{placeholder}</span>;
    }

    const selectedOptions = getSelectedOptions();
    
    if (multiple) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selectedOptions.map((option) => (
            <Chip
              key={option.value}
              label={option.label}
              size="small"
              onDelete={(event) => handleRemoveChip(option.value, event)}
              deleteIcon={<ClearIcon />}
              sx={{ 
                height: 24, 
                fontSize: '0.75rem',
                '& .MuiChip-deleteIcon': {
                  fontSize: '14px'
                }
              }}
            />
          ))}
        </Box>
      );
    } else {
      const option = selectedOptions[0];
      return option ? option.label : '';
    }
  };

  return (
    <FormControl 
      fullWidth={fullWidth} 
      size={size} 
      error={error} 
      variant={variant}
      disabled={disabled}
    >
      <InputLabel required={required}>
        {label}
      </InputLabel>
      
      <Select
        value={(() => {
          if (multiple) {
            return Array.isArray(value) ? value : [];
          } else {
            return value || '';
          }
        })()}
        label={label}
        onChange={handleChange}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setSearchTerm(''); // Limpar busca ao fechar
        }}
        multiple={multiple}
        renderValue={renderValue}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
            },
          },
        }}
        startAdornment={
          <InputAdornment position="start">
            <ReceiptIcon />
          </InputAdornment>
        }
        endAdornment={
          allowClear && value && (
            <InputAdornment position="end">
              <ClearIcon 
                onClick={handleClear}
                sx={{ 
                  cursor: 'pointer', 
                  fontSize: '20px',
                  '&:hover': { color: 'error.main' }
                }}
              />
            </InputAdornment>
          )
        }
      >
        {/* Campo de busca */}
        {open && (
          <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar classe fiscal..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fafafa'
                }
              }}
            />
          </Box>
        )}

        {/* Opção vazia (apenas para seleção única) */}
        {!multiple && !required && (
          <MenuItem value="">
            <em>Nenhuma classe fiscal</em>
          </MenuItem>
        )}

        {/* Lista de opções */}
        {filteredOptions
          .filter(option => option.value !== '' || !multiple) // Filtrar opção vazia em seleção múltipla
          .map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Box display="flex" alignItems="center" width="100%">
                {multiple && (
                  <Checkbox
                    checked={(() => {
                      if (!value) return false;
                      if (Array.isArray(value)) {
                        return value.includes(option.value);
                      }
                      return value === option.value;
                    })()}
                    size="small"
                  />
                )}
                
                <Box display="flex" alignItems="center" gap={1} flex={1}>
                  <ReceiptIcon fontSize="small" />
                  
                  <ListItemText
                    primary={option.label}
                    primaryTypographyProps={{ 
                      fontSize: '0.875rem',
                    }}
                  />
                </Box>
              </Box>
            </MenuItem>
          ))}
      </Select>

      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default ClassFiscalSelect;

